import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { chatbotService, ApiError, ApiErrorBody, ChatbotQueryResponse } from '../../services/chatbot-service';
import { refreshToken } from '../../services/login-service';
import { RootState } from '../../store';
import {
    initializeSession,
    addMessage,
    setLoading,
    setError,
    resetError,
    resetChatbot,
} from '../../store/reducers/chatbotSlice';
import { updateLoginUserDetails } from '../../store/reducers/userSlice';
import { trackChatbotMessageSent, trackChatbotOpened, trackChatbotQuickAction } from '../../utils/google-analytics';
import { colors } from '../theme/theme';

import { ChatInput } from './ChatInput';
import { ChatMessages } from './ChatMessages';
import { formatTime } from './MessageBubbleHelpers';

const DRAWER_WIDTH = 560;
const MAX_QUERY_CHARS = 200;
const CHATBOT_SERVICE_FALLBACK_MESSAGE =
    "I'm here to assist with contracts, payments, invoices, and related support inquiries. However, I'm unable to provide assistance for your question. If you have questions about those or want to see specific contracts, just let me know!";
const CHATBOT_SERVICE_STATUS_MESSAGES: Record<number, string> = {
    500: 'Something went wrong. Please try again in a few moments.',
    502: 'We are experiencing a temporary connection issue. Please try again shortly.',
    503: 'Assistant is temporarily unavailable. Please try again shortly.',
    504: 'Your request is taking longer than expected. Please try again in a few moments.',
};
// Any 5xx not explicitly listed above (501, 505, 511, ...) still gets a friendly server-error line.
const CHATBOT_SERVICE_GENERIC_5XX_MESSAGE = 'Something went wrong on our end. Please try again in a few moments.';
// 4xx responses that arrive without a usable body message (so we can't echo the server's text).
const CHATBOT_CLIENT_ERROR_FALLBACK_MESSAGE =
    "I couldn't complete that request. Please check the details and try again.";

const quickActions = [
    {
        label: 'Contracts',
        subTitle: ' Track & View your contracts',
        icon: 'icon-contracts',
        query: 'Show me my contracts',
    },
    {
        label: 'Payments',
        subTitle: ' View open invoices, scheduled payments & history',
        icon: 'icon-loan',
        query: 'Show me my payment history',
    },
    {
        label: 'Support',
        subTitle: 'Get help with your loans and account',
        icon: 'icon-help',
        query: 'Show me my support tickets',
    },
];

interface QuickActionItemProps {
    query: string;
    label: string;
    subTitle: string;
    icon: string;
    brandPrimaryColor: string;
    onSelect: (query: string, label: string) => void;
    labelComponent: React.ElementType;
    subtitleComponent: React.ElementType;
}

// Shared quick-action tile rendered both in the initial welcome panel and the bottom quick
// actions panel; only the heading levels differ between the two contexts.
const QuickActionItem: React.FC<QuickActionItemProps> = ({
    query,
    label,
    subTitle,
    icon,
    brandPrimaryColor,
    onSelect,
    labelComponent,
    subtitleComponent,
}) => (
    <Stack
        onClick={() => onSelect(query, label)}
        direction="row"
        alignItems="flex-start"
        gap={1.5}
        tabIndex={0}
        onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(query, label);
            }
        }}
        sx={{
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${colors.greyPreset[30]}`,
            backgroundColor: colors.fullWhite,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                borderColor: brandPrimaryColor,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            },
        }}
    >
        <span className={icon} style={{ fontSize: 30, color: brandPrimaryColor, flexShrink: 0, marginTop: 2 }} />
        <Stack alignItems="flex-start" gap={0.3}>
            <Typography variant="h6" component={labelComponent} sx={{ fontWeight: 600, color: colors.fullBlack }}>
                {label}
            </Typography>
            <Typography variant="subtitle2" component={subtitleComponent} sx={{ color: colors.greyPreset[50] }}>
                {subTitle}
            </Typography>
        </Stack>
    </Stack>
);

interface AuxCapAssistantDrawerProps {
    open: boolean;
    onClose: () => void;
}

export const AuxCapAssistantDrawer: React.FC<AuxCapAssistantDrawerProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const sessionId = useSelector((state: RootState) => state.chatbot.sessionId);
    const messages = useSelector((state: RootState) => state.chatbot.messages);
    const isLoading = useSelector((state: RootState) => state.chatbot.isLoading);
    const error = useSelector((state: RootState) => state.chatbot.error);
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const accessToken = useSelector((state: RootState) => state.user.accessToken);
    const refreshTokenValue = useSelector((state: RootState) => state.user.refreshToken);
    const userDetails = useSelector((state: RootState) => state.user.userDetails);
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const [showWelcome, setShowWelcome] = useState(true);
    const [lastFailedQuery, setLastFailedQuery] = useState<string | null>(null);
    const [showBottomQuickActions, setShowBottomQuickActions] = useState(false);
    const lastUserKeyRef = useRef<string | null>(null);
    const wasOpenRef = useRef(false);

    const resolvedPortalId = (() => {
        const id = Number(brandDetails?.Id);
        return Number.isFinite(id) && id > 0 ? id : 2;
    })();

    const brandPrimaryColor = brandDetails?.PrimaryColour ?? colors.bluePreset[10];
    // Strip a trailing "customer portal" suffix without a regex to avoid any
    // backtracking concerns (linear-time, whitespace-tolerant suffix removal).
    const stripCustomerPortalSuffix = (raw: string): string => {
        const trimmed = raw.trim();
        const collapsed = trimmed.toLowerCase().split(/\s+/).join(' ');
        const suffix = 'customer portal';
        if (collapsed.endsWith(suffix)) {
            // Remove the matched words from the end, preserving original casing of the rest.
            const words = trimmed.split(/\s+/);
            return words
                .slice(0, words.length - 2)
                .join(' ')
                .trim();
        }
        return trimmed;
    };
    const brandName = stripCustomerPortalSuffix(brandDetails?.PortalName || '');
    const chatbotTitle = brandName ? `Ask AVA - ${brandName}` : 'Ask AVA';
    const welcomeBrandLine = brandName
        ? `I'm your virtual assistant, AVA for ${brandName}. How can I assist you today?`
        : "I'm your virtual assistant, AVA. How can I assist you today?";

    // Initialize session on first open
    useEffect(() => {
        if (open && !sessionId) {
            const newSessionId = chatbotService.generateSessionId();
            dispatch(initializeSession(newSessionId));
        }
    }, [open, sessionId, dispatch]);

    // GA: fire once each time the chatbot transitions closed -> open (not on every re-render).
    useEffect(() => {
        if (open && !wasOpenRef.current) {
            trackChatbotOpened();
        }
        wasOpenRef.current = open;
    }, [open]);

    // Safety net: if authenticated user identity changes, rotate chatbot session.
    useEffect(() => {
        if (!isLoggedIn) {
            lastUserKeyRef.current = null;
            return;
        }

        const currentUserKey =
            String(userDetails?.Id || '').trim() ||
            String(userDetails?.Email || '')
                .trim()
                .toLowerCase() ||
            String(userDetails?.PhoneNumber || '').trim();

        if (!currentUserKey) {
            return;
        }

        if (lastUserKeyRef.current && lastUserKeyRef.current !== currentUserKey) {
            dispatch(resetChatbot());
            dispatch(initializeSession(chatbotService.generateSessionId()));
        }

        lastUserKeyRef.current = currentUserKey;
    }, [dispatch, isLoggedIn, userDetails]);

    // Hide welcome message when user sends first message
    useEffect(() => {
        if (messages.length > 0) {
            setShowWelcome(false);
        }
    }, [messages.length]);

    const handleClose = () => {
        onClose();
    };

    const handleQuickAction = (query: string, label?: string) => {
        if (impersonation?.isImpersonating) return;
        trackChatbotQuickAction(label || query, query);
        setShowBottomQuickActions(false);
        handleSendMessage(query);
    };

    const CHATBOT_SESSION_EXPIRED_ERROR_CODE = 'CHATBOT_SESSION_EXPIRED';

    const getResponseStatusCode = (response: ChatbotQueryResponse | undefined): number | undefined => {
        const nestedData =
            response?.data && typeof response.data === 'object'
                ? (response.data as Record<string, number | string | undefined>)
                : undefined;

        const rawStatus =
            response?.statusCode ??
            response?.status_code ??
            nestedData?.statusCode ??
            nestedData?.status_code ??
            nestedData?.status;

        const parsed = Number(rawStatus);
        return Number.isFinite(parsed) ? parsed : undefined;
    };

    const getChatbotServiceStatusMessage = (status?: number): string | null => {
        if (!status) return null;
        if (CHATBOT_SERVICE_STATUS_MESSAGES[status]) {
            return CHATBOT_SERVICE_STATUS_MESSAGES[status];
        }
        // Any other server-side failure (501, 505, 511, ...) still shows a friendly line
        // instead of falling through to the raw error/Retry banner.
        if (status >= 500 && status < 600) {
            return CHATBOT_SERVICE_GENERIC_5XX_MESSAGE;
        }
        return null;
    };

    // `data` on an API error can be a raw string or an ApiErrorBody object; only object bodies
    // carry the status/message/error fields. Narrow to the object form (string -> undefined),
    // matching the original optional-chaining behavior without resorting to `any`.
    const asErrorBody = (data: ApiErrorBody | string | undefined): ApiErrorBody | undefined =>
        data && typeof data === 'object' ? data : undefined;

    const getChatbotServiceStatusFromError = (error: ApiError): number | undefined => {
        const responseData = asErrorBody(error?.response?.data);
        const errorData = asErrorBody(error?.data);
        const status = Number(
            error?.response?.status ??
                error?.status ??
                error?.statusCode ??
                error?.status_code ??
                responseData?.statusCode ??
                responseData?.status_code ??
                responseData?.status ??
                errorData?.statusCode ??
                errorData?.status_code,
        );
        if (Number.isFinite(status)) {
            return status;
        }

        const message = String(responseData?.message || error?.message || '').toLowerCase();
        if (message.includes('failed to send message to chatbot service')) {
            return 502;
        }
        return undefined;
    };

    const pushFallbackBotMessage = (messageText?: string) => {
        dispatch(
            addMessage({
                id: `bot-${Date.now()}`,
                type: 'bot',
                text: messageText || CHATBOT_SERVICE_FALLBACK_MESSAGE,
                timestamp: new Date().toISOString(),
            }),
        );
    };

    // A 4xx like 404 ("contract ID not found") carries a helpful human-readable message. Surface
    // that as a normal bot reply instead of the generic error/Retry banner. The chatbot service
    // already pulls the message out and attaches it as `botMessage`; this also re-derives it from
    // the raw payload the axios interceptor rejects with ({ status_code, data: "..." }).
    // Unwrap noisy backend error text into the bare human message:
    //   "HTTP error: {\"statusCode\":404,\"message\":\"Contract 693746 not found for this user\"}"
    //   -> "Contract 693746 not found for this user"
    const cleanChatbotErrorText = (raw: string): string => {
        const trimmed = (raw || '').trim();
        if (!trimmed) {
            return '';
        }
        // Extract the first '{' .. last '}' span via index lookups instead of a regex. This matches
        // the same substring the greedy /\{[\s\S]*\}/ pattern did, but in linear time with no
        // backtracking (avoids any super-linear/ReDoS runtime concern).
        const firstBrace = trimmed.indexOf('{');
        const lastBrace = trimmed.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace > firstBrace) {
            const embeddedJson = trimmed.slice(firstBrace, lastBrace + 1);
            try {
                const parsed = JSON.parse(embeddedJson) as { message?: unknown; error?: unknown };
                const inner =
                    (typeof parsed?.message === 'string' && parsed.message) ||
                    (typeof parsed?.error === 'string' && parsed.error) ||
                    '';
                if (inner.trim()) {
                    return cleanChatbotErrorText(inner);
                }
            } catch {
                // Not valid JSON — fall through to prefix stripping.
            }
        }
        return trimmed.replace(/^(?:HTTP\s+)?error\s*:\s*/i, '').trim();
    };

    // Return the first string-typed field found on a single error body object.
    // Checked in priority order: raw string → data string → message → nested message →
    // nested error (backend wraps useful text here, e.g. { data: { error: "HTTP error: {...}" } }) → error.
    const pickCandidateText = (
        body: ApiErrorBody | string | (ApiError & { botMessage?: string }) | undefined,
    ): string => {
        if (typeof body === 'string') return body;
        const bodyObj: (ApiErrorBody & ApiError) | undefined = body && typeof body === 'object' ? body : undefined;
        if (!bodyObj) return '';
        if (typeof bodyObj.data === 'string') return bodyObj.data;
        const nestedData = asErrorBody(bodyObj.data);
        if (typeof bodyObj.message === 'string') return bodyObj.message;
        if (typeof nestedData?.message === 'string') return nestedData.message;
        if (typeof nestedData?.error === 'string') return nestedData.error;
        if (typeof bodyObj.error === 'string') return bodyObj.error;
        return '';
    };

    // Walk the common error/payload shapes and return the first human-readable message text the
    // backend provided (cleaned of "HTTP error:" noise), or null when none is usable.
    const extractErrorBodyMessage = (error: ApiError & { botMessage?: string }): string | null => {
        const attached = typeof error?.botMessage === 'string' ? error.botMessage.trim() : '';
        if (attached) return cleanChatbotErrorText(attached);

        const bodies: Array<ApiErrorBody | string | (ApiError & { botMessage?: string }) | undefined> = [
            error?.response?.data,
            error?.data,
            error,
        ];
        for (const body of bodies) {
            const trimmed = cleanChatbotErrorText(pickCandidateText(body));
            if (trimmed) return trimmed;
        }
        return null;
    };

    const extractClientErrorBotMessage = (error: ApiError & { botMessage?: string }): string | null => {
        const status = getChatbotServiceStatusFromError(error);
        // 401 (session) is handled by the refresh flow; 5xx maps to the service-status messages.
        if (!status || status < 400 || status >= 500 || status === 401) {
            return null;
        }
        return extractErrorBodyMessage(error);
    };

    // Pull the server's text out of a 5xx response body (e.g. { statusCode: 500, message: "Failed to
    // send message to ChatBot service" }), so the assistant surfaces the actual server message rather
    // than only the generic service-status line. Returns null when no usable text is present.
    const extractServerErrorMessageFromResponse = (response: ChatbotQueryResponse | undefined): string | null =>
        extractErrorBodyMessage({
            response: { data: response as ApiError['data'] },
            data: response as ApiError['data'],
        });

    const sendWithToken = async (userQuery: string, token: string) => {
        if (!sessionId) {
            throw new Error('Session not initialized');
        }
        const response = await chatbotService.sendQuery(
            userQuery,
            sessionId,
            token,
            window.location.origin,
            resolvedPortalId,
        );

        const responseStatus = getResponseStatusCode(response);
        const serviceStatusMessage = getChatbotServiceStatusMessage(responseStatus);
        if (serviceStatusMessage) {
            // 5xx arriving in a 200 body: surface the server's own message (e.g. "Failed to send
            // message to ChatBot service") and keep it retryable. Falls back to the friendly line
            // when the body carries no usable text. Throw so the catch in handleSendMessage owns the
            // error-banner + Retry rendering (lastFailedQuery stays set).
            const serverMessage = extractServerErrorMessageFromResponse(response);
            const serviceError = new Error(serverMessage || serviceStatusMessage) as Error & { statusCode?: number };
            serviceError.statusCode = responseStatus;
            throw serviceError;
        }

        const botMessage = chatbotService.extractBotMessage(response);

        if (chatbotService.isAuthFailureResponse(response, botMessage)) {
            const sessionExpiredError = new Error('Chatbot session expired');
            (sessionExpiredError as Error & { code?: string }).code = CHATBOT_SESSION_EXPIRED_ERROR_CODE;
            throw sessionExpiredError;
        }

        const followUp = chatbotService.extractFollowUp(response);

        dispatch(
            addMessage({
                id: `bot-${Date.now()}`,
                type: 'bot',
                text: botMessage,
                timestamp: new Date().toISOString(),
                query: userQuery,
                followUp,
            }),
        );
    };

    // Returns true when handled (caller should stop processing).
    const handleServiceError = (err: ApiError & { botMessage?: string }): boolean => {
        const serviceStatus = getChatbotServiceStatusFromError(err);
        const serviceStatusMessage = getChatbotServiceStatusMessage(serviceStatus);
        if (!serviceStatusMessage) return false;

        // 5xx: surface the server's own message; keep lastFailedQuery so banner offers Retry.
        const serverMessage = extractErrorBodyMessage(err);
        dispatch(setError(serverMessage || serviceStatusMessage));
        return true;
    };

    // Returns true when handled (4xx non-401 → bot reply, no Retry).
    const handleClientError = (err: ApiError & { botMessage?: string }): boolean => {
        const serviceStatus = getChatbotServiceStatusFromError(err);
        const isClientError = serviceStatus !== undefined && serviceStatus >= 400 && serviceStatus < 500;
        if (!isClientError || serviceStatus === 401) return false;

        dispatch(resetError());
        pushFallbackBotMessage(extractClientErrorBotMessage(err) || CHATBOT_CLIENT_ERROR_FALLBACK_MESSAGE);
        setLastFailedQuery(null);
        return true;
    };

    // Normalizes an unknown catch value into the ApiError shape used throughout error handling.
    const toApiError = (caught: unknown): ApiError & { botMessage?: string } =>
        (typeof caught === 'object' && caught !== null ? caught : {}) as ApiError & { botMessage?: string };

    // Attempts the token refresh + retry; returns the new token on success, or null when it didn't apply/succeed.
    const tryRefreshToken = async (normalizedQuery: string, currentRefreshToken: string): Promise<string | null> => {
        const response = await refreshToken(currentRefreshToken, false);
        if (response?.statusCode !== 200 && !response?.data?.token) return null;

        const newToken = response.data?.token || response.data?.accessToken || response.accessToken;
        const newRefreshToken = response.data?.refreshToken || response?.refreshToken || refreshTokenValue;
        dispatch(updateLoginUserDetails({ token: newToken, refreshToken: newRefreshToken, user: userDetails ?? {} }));
        if (!newToken) return null;

        await sendWithToken(normalizedQuery, newToken);
        return newToken;
    };

    // Returns the new error to surface after a refresh attempt, or null when refresh succeeded.
    const handleTokenRefresh = async (
        normalizedQuery: string,
        err: ApiError & { botMessage?: string },
    ): Promise<(ApiError & { botMessage?: string }) | null> => {
        const isUnauthorized = err?.response?.status === 401;
        const isChatbotSessionExpired = err?.code === CHATBOT_SESSION_EXPIRED_ERROR_CODE;
        if ((!isUnauthorized && !isChatbotSessionExpired) || !refreshTokenValue) return err;

        try {
            const refreshedToken = await tryRefreshToken(normalizedQuery, refreshTokenValue);
            if (!refreshedToken) return err;
            setLastFailedQuery(null);
            return null;
        } catch (refreshErr: unknown) {
            console.error('Refresh token failed:', refreshErr);
            return toApiError(refreshErr);
        }
    };

    const buildFinalErrorMessage = (finalErr: ApiError & { botMessage?: string }): string => {
        if (finalErr?.code === CHATBOT_SESSION_EXPIRED_ERROR_CODE) {
            return 'Unable to process your request. Please try again.';
        }
        return finalErr instanceof Error ? finalErr.message : 'Failed to get response';
    };

    const handleSendError = async (err: ApiError & { botMessage?: string }, normalizedQuery: string) => {
        if (handleServiceError(err)) return;
        if (handleClientError(err)) return;

        const finalErr = await handleTokenRefresh(normalizedQuery, err);
        if (!finalErr) return;

        dispatch(setError(buildFinalErrorMessage(finalErr)));
        console.error('Chat error:', finalErr);
    };

    const dispatchUserMessage = (normalizedQuery: string) => {
        dispatch(
            addMessage({
                id: `user-${Date.now()}`,
                type: 'user',
                text: normalizedQuery,
                timestamp: new Date().toISOString(),
            }),
        );
    };

    const handleSendMessage = async (userQuery: string, options?: { isRetry?: boolean }) => {
        // Prevent chatbot interaction in impersonate mode
        if (impersonation?.isImpersonating) return;

        const normalizedQuery = userQuery.trim();
        if (!sessionId || !normalizedQuery) return;

        if (userQuery.length > MAX_QUERY_CHARS) {
            dispatch(setError(`Please keep your query within ${MAX_QUERY_CHARS} characters.`));
            return;
        }

        setShowBottomQuickActions(false);
        if (!options?.isRetry) {
            dispatchUserMessage(normalizedQuery);
        }

        dispatch(setLoading(true));
        dispatch(resetError());

        try {
            await sendWithToken(normalizedQuery, accessToken);
            setLastFailedQuery(null);
        } catch (caught: unknown) {
            setLastFailedQuery(normalizedQuery);
            await handleSendError(toApiError(caught), normalizedQuery);
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleRetry = () => {
        if (!lastFailedQuery || isLoading) {
            return;
        }
        handleSendMessage(lastFailedQuery, { isRetry: true });
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{
                role: 'dialog',
                'aria-modal': true,
                'aria-label': chatbotTitle,
                sx: {
                    width: DRAWER_WIDTH,
                    maxWidth: '100%',
                    boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
                    borderRadius: '12px 0 0 12px',
                    overflow: 'hidden',
                },
            }}
        >
            <Stack
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '& .MuiTypography-root': {
                        lineHeight: '21px',
                    },
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        backgroundColor: brandPrimaryColor,
                        p: 2,
                        gap: 1.5,
                        border: 'none',
                        borderRadius: '0',
                        borderBottom: 'none',
                    }}
                >
                    <Stack direction="row" alignItems="center" gap={1.5} flex={1}>
                        <span className="icon-rounded-chat" style={{ fontSize: 24, color: colors.fullWhite }} />
                        <Typography
                            variant="h6"
                            component="h1"
                            sx={{ color: colors.fullWhite, fontWeight: 600, fontSize: '18px' }}
                        >
                            {chatbotTitle}
                        </Typography>
                    </Stack>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        aria-label="Close"
                        sx={{ color: colors.fullWhite, p: 0 }}
                    >
                        <span className="icon-close" style={{ fontSize: 20 }} />
                    </IconButton>
                </Stack>

                {/* Chat Messages Area */}
                <Stack
                    sx={{
                        flex: 1,
                        px: 1,
                        py: 2,
                        gap: 2,
                        backgroundColor: alpha(brandPrimaryColor, 0.04),
                        overflowY: 'auto',
                    }}
                >
                    {messages.length === 0 && showWelcome ? (
                        <Stack alignItems="flex-start" gap={1.5}>
                            <Stack direction="row" gap={1} alignItems="flex-start">
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        backgroundColor: brandPrimaryColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <span
                                        className="icon-rounded-chat"
                                        style={{ fontSize: 18, color: colors.fullWhite }}
                                    />
                                </Box>
                                <Stack gap={0.5}>
                                    <Typography
                                        variant="h6"
                                        component="h2"
                                        sx={{ fontWeight: 600, color: colors.fullBlack }}
                                    >
                                        {`Welcome ${
                                            userDetails?.FirstName && userDetails?.LastName
                                                ? `${userDetails.FirstName} ${userDetails.LastName}`
                                                : userDetails?.Name
                                        }`}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: colors.fullBlack, lineHeight: 1.6 }}>
                                        {welcomeBrandLine}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: colors.fullBlack, lineHeight: 1.6, mt: 0.5 }}
                                    >
                                        You can choose an option below or ask me anything.
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Typography variant="caption" sx={{ color: colors.greyPreset[50] }}>
                                {formatTime(new Date())}
                            </Typography>
                        </Stack>
                    ) : (
                        <ChatMessages
                            messages={messages}
                            isLoading={isLoading}
                            error={error}
                            onRetry={handleRetry}
                            canRetry={Boolean(lastFailedQuery)}
                            onShowQuickActions={() => setShowBottomQuickActions(true)}
                        />
                    )}

                    {/* Quick actions - shown only on initial welcome */}
                    {messages.length === 0 && showWelcome && (
                        <Stack gap={1.5} sx={{ mt: 2, width: '100%' }}>
                            <Typography variant="subtitle1" component={'h2'} color="textPrimary">
                                Quick actions:
                            </Typography>
                            {quickActions?.map(({ label, query, subTitle, icon }) => (
                                <QuickActionItem
                                    key={query}
                                    query={query}
                                    label={label}
                                    subTitle={subTitle}
                                    icon={icon}
                                    brandPrimaryColor={brandPrimaryColor}
                                    onSelect={handleQuickAction}
                                    labelComponent="h3"
                                    subtitleComponent="h4"
                                />
                            ))}
                        </Stack>
                    )}

                    {/* Bottom Quick Actions Panel */}
                    {showBottomQuickActions && (
                        <Stack
                            gap={1.5}
                            sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.greyPreset[20]}`, width: '100%' }}
                        >
                            <Typography variant="subtitle1" color="textPrimary">
                                Quick actions:
                            </Typography>
                            {quickActions?.map(({ label, query, subTitle, icon }) => (
                                <QuickActionItem
                                    key={query}
                                    query={query}
                                    label={label}
                                    subTitle={subTitle}
                                    icon={icon}
                                    brandPrimaryColor={brandPrimaryColor}
                                    onSelect={handleQuickAction}
                                    labelComponent="h2"
                                    subtitleComponent="h3"
                                />
                            ))}
                        </Stack>
                    )}
                </Stack>

                {/* Chat Input */}
                <ChatInput
                    onSendMessage={(query: string) => {
                        trackChatbotMessageSent();
                        handleSendMessage(query);
                    }}
                    isLoading={isLoading}
                    brandPrimaryColor={brandPrimaryColor}
                />
            </Stack>
        </Drawer>
    );
};
